import {KycRecord} from "../../domain/kyc.entity";
import {
  KycStatus,
  KycSocialLinks,
  KycSocialLinkKey,
  CreateKycParams,
  UpdateKycParams,
  UploadKycDocumentsResult,
} from "../../domain/kyc.types";
import {
  V2KycResponse,
  V2SocialLinkResponseKey,
  V2SocialLinkRequestKey,
  V2CreateKycRequest,
  V2UpdateKycRequest,
  V2UploadDocumentsResponse,
} from "./kyc.mapper.types";

/**
 * Maps Title Case API response social link keys to camelCase domain keys.
 */
const SOCIAL_LINK_RESPONSE_TO_DOMAIN: Record<V2SocialLinkResponseKey, KycSocialLinkKey> =
  {
    Instagram: "instagram",
    LinkedIn: "linkedIn",
    Facebook: "facebook",
    "Twitter/X": "twitterX",
    YouTube: "youTube",
    TikTok: "tikTok",
    Snapchat: "snapchat",
    Twitch: "twitch",
    Kick: "kick",
    Pinterest: "pinterest",
    Threads: "threads",
  };

/**
 * Maps numeric API status codes to domain KycStatus.
 * Used as fallback when statusName is not available.
 */
const STATUS_CODE_MAP: Record<number, KycStatus> = {
  1: "Active",
  4: "InProgress",
  8: "Failed",
  12: "Submitted",
};

/**
 * Maps string statusName from API to domain KycStatus.
 */
const STATUS_NAME_MAP: Record<string, KycStatus> = {
  "In Progress": "InProgress",
  Submitted: "Submitted",
  Active: "Active",
  Failed: "Failed",
};

/**
 * Maps v2 API responses to KycRecord domain entities.
 *
 * Key responsibilities:
 * - Converts Title Case social link keys to camelCase
 * - Normalizes numeric status code + statusName to KycStatus union
 * - Handles null/missing fields gracefully
 *
 * Pure TypeScript - no React imports.
 */
export const KycMapperV2 = {
  /**
   * Maps a v2 KYC API response to a KycRecord entity.
   */
  toDomain(response: V2KycResponse, userId?: number): KycRecord {
    return KycRecord.create({
      id: response.id,
      userId: response.userId ?? userId ?? 0,
      fullName: response.fullName,
      businessName: response.businessName ?? "",
      serviceDescription: response.serviceDescription ?? "",
      email: response.email,
      socialLinks: this.mapSocialLinksFromResponse(response.socialLinks),
      country: response.country,
      address: response.address,
      postalCode: response.postalCode,
      phoneNumber: response.phoneNumber,
      gender: response.gender ?? "",
      dateOfBirth: response.dateOfBirth ?? "",
      documents: {
        proofOfAddressUrl: response.proofOfAddressUrl,
        governmentIdUrl: response.governmentIdUrl,
        selfieWithIdUrl: response.selfieWithIdUrl,
        businessRegistrationUrl: response.businessRegistrationUrl,
      },
      status: this.mapStatus(response.status, response.statusName),
      reasonForRejection: response.reasonForRejection,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
    });
  },

  /**
   * Unwraps data envelope from GET kyc and maps to KycRecord.
   */
  fromGetResponse(response: {data: V2KycResponse}): KycRecord {
    return this.toDomain(response.data);
  },

  /**
   * Converts Title Case social link keys from API response to camelCase domain keys.
   */
  mapSocialLinksFromResponse(
    links: Partial<Record<V2SocialLinkResponseKey, string>> | null
  ): KycSocialLinks {
    if (!links) return {};

    const result: KycSocialLinks = {};
    for (const [key, value] of Object.entries(links)) {
      const domainKey = SOCIAL_LINK_RESPONSE_TO_DOMAIN[key as V2SocialLinkResponseKey];
      if (domainKey && value) {
        result[domainKey] = value;
      }
    }
    return result;
  },

  /**
   * Normalizes API numeric status + optional statusName to domain KycStatus.
   * Prefers statusName when available; falls back to numeric code mapping.
   */
  mapStatus(statusCode: number, statusName?: string): KycStatus {
    if (statusName && STATUS_NAME_MAP[statusName]) {
      return STATUS_NAME_MAP[statusName];
    }
    return STATUS_CODE_MAP[statusCode] ?? "InProgress";
  },

  // ==================== Request Mappers (Domain → API) ====================

  /**
   * Converts CreateKycParams to v2 API create request.
   * Social links are already camelCase in domain, so pass through directly.
   * Status is always "In Progress" (with space) for API.
   */
  toCreateRequest(params: CreateKycParams): V2CreateKycRequest {
    return {
      userId: params.userId,
      fullName: params.fullName,
      businessName: params.businessName,
      serviceDescription: params.serviceDescription,
      email: params.email,
      socialLinks: params.socialLinks as Partial<Record<V2SocialLinkRequestKey, string>>,
      country: params.country,
      address: params.address,
      postalCode: params.postalCode,
      phoneNumber: params.phoneNumber,
      gender: params.gender,
      dateOfBirth: params.dateOfBirth,
      proofOfAddressUrl: params.documents.proofOfAddressUrl,
      governmentIdUrl: params.documents.governmentIdUrl,
      selfieWithIdUrl: params.documents.selfieWithIdUrl,
      businessRegistrationUrl: params.documents.businessRegistrationUrl,
      status: "In Progress",
    };
  },

  /**
   * Converts UpdateKycParams to v2 API update request.
   * Extends create request with kycId.
   */
  toUpdateRequest(params: UpdateKycParams): V2UpdateKycRequest {
    return {
      ...this.toCreateRequest(params),
      kycId: params.kycId,
    };
  },

  /**
   * Unwraps data envelope from POST/PATCH kyc and maps to KycRecord.
   */
  fromCreateResponse(response: {data: V2KycResponse}, userId: number): KycRecord {
    return this.toDomain(response.data, userId);
  },

  // ==================== Upload Documents Mappers ====================

  /**
   * Extracts document URLs from upload-documents response.
   */
  toUploadDocumentsResult(response: V2UploadDocumentsResponse): UploadKycDocumentsResult {
    return {
      proofOfAddressUrl: response.data.proofOfAddressUrl,
      governmentIdUrl: response.data.governmentIdUrl,
      selfieWithIdUrl: response.data.selfieWithIdUrl,
      businessRegistrationUrl: response.data.businessRegistrationUrl,
    };
  },
};
